import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Ban, Unlock, RotateCcw, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { useRefData } from "@/hooks/useRefData";
import { inputClass } from "@/components/FieldInput";
import { PageHeader, Card, StatusBadge } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { AccessLettersButton } from "@/components/AccessLettersButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const PRIV = ["admin", "gestore_utenti"];
const MFA = ["admin", "gestore_utenti", "commercialista"];

function UserForm({ initial, onClose, onSaved, trusts, isAdmin }) {
  const [f, setF] = useState(initial);
  const roles = Object.keys(ROLE_LABELS).filter((r) => isAdmin || !PRIV.includes(r));
  const save = async () => {
    try {
      if (f.id) await api.put(`/users/${f.id}`, { name: f.name, role: f.role, trust_ids: f.trust_ids, authorization_ref: f.authorization_ref, access_expires_on: f.access_expires_on || null, ...(isAdmin && !PRIV.includes(f.role) ? { can_log_activities: !!f.can_log_activities } : {}) });
      else await api.post("/users", { ...f, access_expires_on: f.access_expires_on || null, can_log_activities: isAdmin && !PRIV.includes(f.role) && !!f.can_log_activities });
      toast.success(f.id ? "Diritti aggiornati" : "Utenza creata — email di attivazione inviata");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (id) => setF((s) => ({ ...s, trust_ids: s.trust_ids.includes(id) ? s.trust_ids.filter((x) => x !== id) : [...s.trust_ids, id] }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{f.id ? "Modifica diritti di accesso" : "Nuova utenza"}</DialogTitle>
          <DialogDescription>Utenza nominativa: l'identificativo è l'email personale. Accesso con OTP, nessuna password da consegnare.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input className={inputClass} placeholder="Email nominativa" disabled={!!f.id} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" />
          <input className={inputClass} placeholder="Nome e cognome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" />
          <select className={inputClass} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role-select">
            {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <input className={inputClass} placeholder="Riferimento richiesta/autorizzazione (es. email del 12/09/2026)" value={f.authorization_ref || ""} onChange={(e) => setF({ ...f, authorization_ref: e.target.value })} data-testid="user-authref-input" />
          <div>
            <label className="text-xs font-semibold text-slate-600">Scadenza esigenza di accesso (facoltativa)</label>
            <input type="date" className={inputClass} value={f.access_expires_on || ""} onChange={(e) => setF({ ...f, access_expires_on: e.target.value })} data-testid="user-expiry-input" />
          </div>
          {!PRIV.includes(f.role) && (
            <div>
              <label className={`flex items-start gap-3 rounded-lg border p-3 ${isAdmin ? "border-slate-200 hover:bg-slate-50 cursor-pointer" : "border-slate-100 bg-slate-50/60"}`} data-testid="user-can-log-activities">
                <input type="checkbox" className="accent-[#0A2540] mt-0.5" disabled={!isAdmin} checked={!!f.can_log_activities} onChange={(e) => setF({ ...f, can_log_activities: e.target.checked })} data-testid="user-can-log-activities-input" />
                <span>
                  <span className="block text-sm font-semibold text-slate-800">Può registrare attività</span>
                  <span className="block text-xs text-slate-500">Inserimento, modifica ed eliminazione delle attività nei Trust collegati. {isAdmin ? "" : "Impostabile solo dall'Amministratore."}</span>
                </span>
              </label>
            </div>
          )}
          {!PRIV.includes(f.role) && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Trust collegati (segregazione per Trust)</p>
              <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1" data-testid="user-trusts-list">
                {trusts.length === 0 && <p className="text-xs text-slate-400 p-2">Nessun Trust disponibile</p>}
                {trusts.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-slate-50">
                    <input type="checkbox" className="accent-[#0A2540]" checked={f.trust_ids.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`user-trust-${t.id}`} />{t.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="user-form-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user } = useAuth();
  const { trusts = [] } = useRefData(["trusts"]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const isAdmin = user.role === "admin";

  const statusAction = async (u, action, label) => {
    const reason = window.prompt(`Motivo (${label}):`);
    if (!reason) return;
    try { await api.post(`/users/${u.id}/${action}`, { reason }); toast.success(`Utenza: ${label}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const trustNames = (ids) => ids?.map((id) => trusts.find((t) => t.id === id)?.name).filter(Boolean).join(", ") || "—";

  return (
    <div>
      <PageHeader eyebrow="TM-ISMS-06 · A.5.15 / A.5.16 / A.5.18" title="Gestione Utenti" subtitle="Ciclo di vita delle identità: creazione nominativa, modifica dei diritti, sospensione, sblocco. Ogni operazione è registrata nel log di audit.">
        <AccessLettersButton />
        <Button data-testid="user-new-button" className="bg-[#0A2540] hover:bg-[#031B33] text-white" onClick={() => setForm({ email: "", name: "", role: "collaboratore", trust_ids: [], authorization_ref: "" })}><Plus className="h-4 w-4 mr-1" />Nuova utenza</Button>
      </PageHeader>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm text-left" data-testid="users-table">
          <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">Utente</th><th className="px-4 py-3">Profilo</th><th className="px-4 py-3">Trust collegati</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Ultimo accesso</th><th className="px-4 py-3">AUP</th><th className="px-4 py-3">App 2FA</th><th className="px-4 py-3 text-right">Azioni</th></tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const locked = !isAdmin && PRIV.includes(u.role);
              const glass = user.role === "gestore_utenti" && u.role === "admin" && !rows.some((x) => x.role === "admin" && x.status === "attivo" && x.id !== u.id);
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3"><p className="font-medium text-slate-900">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
                  <td className="px-4 py-3">{ROLE_LABELS[u.role]}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[220px] truncate">{PRIV.includes(u.role) ? "Tutti" : trustNames(u.trust_ids)}</td>
                  <td className="px-4 py-3"><StatusBadge value={u.status} testId={`user-status-${u.email}`} />{(u.locked_reason || u.suspended_reason) && <p className="text-[11px] text-slate-400 mt-1">{u.locked_reason || u.suspended_reason}</p>}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-xs">{u.aup_accepted_at ? "Accettata" : <span className="text-amber-600">In attesa</span>}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`user-mfa-${u.email}`}>{u.totp_enabled ? <span className="text-emerald-700">Attiva</span> : MFA.includes(u.role) ? <span className="text-amber-600">Da registrare</span> : "—"}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {!locked && u.id !== user.id && (
                      <>
                        <Button variant="ghost" size="icon" title="Modifica" data-testid={`user-edit-${u.email}`} onClick={() => setForm({ ...u, trust_ids: u.trust_ids || [] })}><Pencil className="h-4 w-4" /></Button>
                        {u.totp_enabled && <Button variant="ghost" size="icon" title="Azzera app autenticatore" className="text-slate-600" data-testid={`user-reset-totp-${u.email}`} onClick={() => statusAction(u, "reset-totp", "azzeramento app autenticatore")}><Smartphone className="h-4 w-4" /></Button>}
                        {u.status === "attivo" && <Button variant="ghost" size="icon" title="Sospendi" className="text-amber-600" data-testid={`user-suspend-${u.email}`} onClick={() => statusAction(u, "suspend", "sospensione")}><Ban className="h-4 w-4" /></Button>}
                        {u.status === "bloccato" && <Button variant="ghost" size="icon" title="Sblocca" className="text-emerald-600" data-testid={`user-unlock-${u.email}`} onClick={() => statusAction(u, "unlock", "sblocco")}><Unlock className="h-4 w-4" /></Button>}
                        {u.status === "sospeso" && <Button variant="ghost" size="icon" title="Riattiva" className="text-emerald-600" data-testid={`user-reactivate-${u.email}`} onClick={() => statusAction(u, "reactivate", "riattivazione")}><RotateCcw className="h-4 w-4" /></Button>}
                      </>
                    )}
                    {locked && glass && (
                      <>
                        {u.totp_enabled && <Button variant="ghost" size="icon" title="Emergenza: azzera app autenticatore dell'Amministratore" className="text-red-600" data-testid={`user-breakglass-reset-totp-${u.email}`} onClick={() => statusAction(u, "reset-totp", "azzeramento di emergenza app autenticatore")}><Smartphone className="h-4 w-4" /></Button>}
                        {u.status === "bloccato" && <Button variant="ghost" size="icon" title="Emergenza: sblocca l'Amministratore" className="text-red-600" data-testid={`user-breakglass-unlock-${u.email}`} onClick={() => statusAction(u, "unlock", "sblocco di emergenza")}><Unlock className="h-4 w-4" /></Button>}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      {form && <UserForm initial={form} trusts={trusts} isAdmin={isAdmin} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}
